import {
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
} from "lucide-react";
import type * as React from "react";

/**
 * Utility functions for working with files
 */

/**
 * Formats a file size in bytes to a human-readable string
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size (e.g., "1.5 MB", "500 KB", "2.3 GB")
 *
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1536000) // "1.5 MB"
 * formatFileSize(5242880) // "5 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 Bytes";
  }

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

/**
 * Validates if a file matches the accepted MIME types
 *
 * @param file - File to validate
 * @param accept - Comma-separated list of MIME types or file extensions (e.g., "image/*,application/pdf,.doc")
 * @returns true if the file type is accepted
 *
 * @example
 * validateFileType(file, "image/*") // true for any image
 * validateFileType(file, "application/pdf") // true only for PDFs
 * validateFileType(file, ".jpg,.png") // true for JPG and PNG
 */
export function validateFileType(file: File, accept?: string): boolean {
  if (!accept) {
    return true;
  }

  const acceptedTypes = accept.split(",").map((type) => type.trim());

  for (const type of acceptedTypes) {
    // Handle wildcard MIME types (e.g., "image/*")
    if (type.includes("/*")) {
      const baseType = type.split("/")[0];
      if (file.type.startsWith(`${baseType}/`)) {
        return true;
      }
    }

    // Handle specific MIME types (e.g., "application/pdf")
    if (type === file.type) {
      return true;
    }

    // Handle file extensions (e.g., ".pdf")
    if (
      type.startsWith(".") &&
      file.name.toLowerCase().endsWith(type.toLowerCase())
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Validates if a file size is within the maximum allowed size
 *
 * @param file - File to validate
 * @param maxSize - Maximum file size in bytes
 * @returns true if the file size is valid
 *
 * @example
 * validateFileSize(file, 5 * 1024 * 1024) // true if file is <= 5MB
 * validateFileSize(file, 1048576) // true if file is <= 1MB
 */
export function validateFileSize(file: File, maxSize?: number): boolean {
  if (!maxSize) {
    return true;
  }
  return file.size <= maxSize;
}

/**
 * Gets the file extension from a filename
 *
 * @param filename - Filename to extract extension from
 * @returns File extension including the dot (e.g., ".pdf", ".jpg") or empty string if no extension
 *
 * @example
 * getFileExtension("document.pdf") // ".pdf"
 * getFileExtension("image.jpeg") // ".jpeg"
 * getFileExtension("file") // ""
 */
export function getFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf(".");
  return lastDotIndex === -1 ? "" : filename.slice(lastDotIndex);
}

/**
 * Gets an appropriate icon component for a file based on its type
 *
 * @param file - File to get icon for
 * @returns Lucide React icon component
 *
 * @example
 * const Icon = getFileIcon(file);
 * <Icon className="size-4" />
 */
export function getFileIcon(
  file: File
): React.ComponentType<{ className?: string }> {
  const type = file.type;
  const extension = getFileExtension(file.name).toLowerCase();

  // Images
  if (type.startsWith("image/")) {
    return FileImage;
  }

  // PDFs
  if (type === "application/pdf" || extension === ".pdf") {
    return FileText;
  }

  // Spreadsheets
  if (
    type.includes("spreadsheet") ||
    type.includes("excel") ||
    [".xlsx", ".xls", ".csv"].includes(extension)
  ) {
    return FileSpreadsheet;
  }

  // Videos
  if (type.startsWith("video/")) {
    return FileVideo;
  }

  // Text files
  if (
    type.startsWith("text/") ||
    [".txt", ".md", ".json", ".xml"].includes(extension)
  ) {
    return FileText;
  }

  // Default generic file icon
  return FileIcon;
}

/**
 * Creates a preview URL for an image file
 *
 * @param file - Image file to create preview for
 * @returns Promise that resolves to a data URL for the image
 *
 * @example
 * const preview = await createImagePreview(file);
 * <img src={preview} alt="Preview" />
 */
export function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    // Validate that it's an image
    if (!file.type.startsWith("image/")) {
      reject(new Error("File is not an image"));
      return;
    }

    const reader = new FileReader();

    reader.addEventListener("load", () => {
      resolve(reader.result as string);
    });

    reader.addEventListener("error", () => {
      reject(new Error("Failed to read file"));
    });

    reader.readAsDataURL(file);
  });
}

/**
 * Validates a file against multiple criteria
 *
 * @param file - File to validate
 * @param options - Validation options
 * @returns Error message if validation fails, undefined if valid
 *
 * @example
 * const error = validateFile(file, {
 *   accept: "image/*",
 *   maxSize: 5 * 1024 * 1024,
 * });
 * if (error) {
 *   console.error(error);
 * }
 */
export function validateFile(
  file: File,
  options?: {
    accept?: string;
    maxSize?: number;
  }
): string | undefined {
  // Check file type
  if (options?.accept && !validateFileType(file, options.accept)) {
    return `File type "${file.type}" is not accepted`;
  }

  // Check file size
  if (options?.maxSize && !validateFileSize(file, options.maxSize)) {
    return `File size ${formatFileSize(file.size)} exceeds maximum of ${formatFileSize(options.maxSize)}`;
  }

  return undefined;
}
